import TikTokRichActions from 'src/pages/TikTok/components/Capabilities/TikTokRichComposer.component';
import classNames from 'classnames';
import { BoltIcon, ClockIcon } from '@heroicons/react/24/outline';
import { CalendarIcon } from '@heroicons/react/24/solid';
import { Button, Tooltip } from 'react-daisyui';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from 'src/hooks/analytics/useAnalytics.hook';
import { useFeatureFlagsContext } from '../../../../../../providers/FeatureFlagsProvider/FeatureFlags.context';
import { useBoardSlice } from 'src/pages/Pipe/PipeBoard.context';
import { useWorkspaceContext } from '../../../../../../providers/WorkspaceProvider/Workspace.context.ts';
import {
  eChannelType,
  isSLBSupportedChannel,
} from '../../../../../../providers/SocketsProvider/Sockets.enums';
import { isTikTokChannel } from '../../../../../../constants/channel.constants';
import { ChatEnum } from '../../../../Chat.enum.ts';
import { useChatContext } from '../../../../Chat.hook';
import { useMessageBarContext } from '../../MessageBar.hook.ts';
import { useModalsContext } from '../../../../../../providers/ModalsProvider/Modals.context.ts';
import './ToolActions.styles.scss';
import AudioRecorderAction from './components/AudioRecorderAction/AudioRecorderAction.component.tsx';
import AIResponseAction from './components/AIResponseAction/AIResponseAction.component.tsx';
import ConversationSummaryAction from './components/ConversationSummaryAction/ConversationSummaryAction.component.tsx';
import SendAction from './components/SendAction/SendAction.component.tsx';
import SuggestedResponsesAction from './components/SuggestedResponsesAction/SuggestedResponsesAction.component.tsx';
import { trackActionsEvent } from './ToolActions.functions';
import type { tDisabledReasonInfo } from './components/AutoRespondToggle/AutoRespondToggle.hook';

type tToolActionsProps = {
  autoRespondEnabled: boolean;
  autoRespondSaving: boolean;
  onAutoRespondToggle: () => void;
  autoRespondDisabledReason: tDisabledReasonInfo | null;
  onIntervene?: () => void;
  onGuide?: () => void;
};

export default function ToolActions(props: tToolActionsProps) {
  const {
    autoRespondEnabled,
    autoRespondSaving,
    onAutoRespondToggle,
    autoRespondDisabledReason,
  } = props;
  const { isSmartLeadBotEnabled } = useFeatureFlagsContext();
  const { setSelectedAction, selectedAction, passDataToLegacyInput } =
    useMessageBarContext();
  const { selectedChat } = useChatContext();
  const isSLBChat = isSLBSupportedChannel(selectedChat?.integration);
  const { t } = useTranslation('chat/chat');
  const isTiktokChat = isTikTokChannel(selectedChat?.integration);

  const { checkIfWorkspaceHasMoreThan90Days } = useWorkspaceContext();
  const { currentFunnel } = useBoardSlice();
  const { trackEvent } = useAnalytics();
  const { selectedLeadForChatModal } = useModalsContext();
  const isWapiActive =
    selectedLeadForChatModal?.chat?.integration === eChannelType.WHATSAPP_API;

  const isQuickActions =
    selectedAction === ChatEnum.QUICK_ACTIONS ||
    selectedAction === ChatEnum.CREATE_QUICK_ACTION;

  const isChatEvents = selectedAction === ChatEnum.EVENTS;
  const isProgrammedMessages =
    selectedAction === ChatEnum.PROGRAMMED_MESSAGES ||
    selectedAction === ChatEnum.WAPI_SCHEDULE_SELECTOR;

  const analyticsActionsEvent = (event: string) => {
    const isFromIndustryTemplate =
      currentFunnel?.fromIndustryTemplate === true &&
      !checkIfWorkspaceHasMoreThan90Days();

    trackActionsEvent(trackEvent, event, isFromIndustryTemplate);
  };

  return (
    <div className="ToolActions items-center">
      <div
        className={classNames(
          'ToolActions__AI_Card ToolActions__AI_Multichase',
          {
            'ToolActions__AI_Card--active':
              isSmartLeadBotEnabled && isSLBChat && autoRespondEnabled,
          },
        )}
      >
        <SuggestedResponsesAction
          tooltipMessage={t('toolActions.suggestedResponses')}
        />

        <ConversationSummaryAction
          tooltipMessage={t('toolActions.conversationSummary')}
        />

        {isSmartLeadBotEnabled && isSLBChat && (
          <AIResponseAction
            tooltipMessage={t('toolActions.generateResponse')}
            autoRespondEnabled={autoRespondEnabled}
            autoRespondSaving={autoRespondSaving}
            onAutoRespondToggle={onAutoRespondToggle}
            autoRespondDisabledReason={autoRespondDisabledReason}
            onIntervene={props.onIntervene}
            onGuide={props.onGuide}
          />
        )}
      </div>

      {isTiktokChat && <TikTokRichActions key={selectedChat?.id} />}
      <Tooltip message={t('toolActions.quickActions')}>
        <Button
          color="ghost"
          onClick={() => {
            setSelectedAction(ChatEnum.QUICK_ACTIONS);
            analyticsActionsEvent('quick_actions');
          }}
          size="xs"
          className="MessageBar__Button"
          active={isQuickActions}
          data-tour="chat-quick-actions-button"
        >
          <BoltIcon className="MessageBar__Icon" />
        </Button>
      </Tooltip>

      <Tooltip message={t('toolActions.scheduleEvent')}>
        <Button
          color="ghost"
          onClick={() => {
            setSelectedAction(ChatEnum.EVENTS);
            analyticsActionsEvent('agenda');
          }}
          size="xs"
          className="MessageBar__Button"
          active={isChatEvents}
          data-tour="chat-schedule-event-button"
        >
          <CalendarIcon className="MessageBar__Icon" />
        </Button>
      </Tooltip>

      <Tooltip message={t('toolActions.scheduleMessage')}>
        <Button
          color="ghost"
          onClick={() => {
            if (isWapiActive) {
              setSelectedAction(ChatEnum.WAPI_SCHEDULE_SELECTOR);
            } else {
              passDataToLegacyInput(ChatEnum.PROGRAMMED_MESSAGES);
            }
          }}
          size="xs"
          className="MessageBar__Button"
          active={isProgrammedMessages}
        >
          <ClockIcon className="MessageBar__Icon" />
        </Button>
      </Tooltip>

      {!isTiktokChat && (
        <Tooltip message={t('toolActions.audioRecord')} position="top">
          <AudioRecorderAction />
        </Tooltip>
      )}

      <Tooltip
        message={t('toolActions.send')}
        position="top"
        className="flex items-center"
      >
        <SendAction />
      </Tooltip>
    </div>
  );
}
